import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatPreferredDate } from '../../utils/preferredDate';
import { API_BASE_URL } from '../../utils/apiBase';
import { deriveClientJourney, isFinanciallySecured } from '../../utils/clientJourney';
import { formatQuoteMoney } from '../../utils/quotePricing';
import { clientFacingShootType, StoredQuote } from '../../utils/quoteRecord';
import { clientKeyForQuote } from '../../utils/clientKey';
import CorrespondenceTimeline, { TimelineEntry } from '../Quote/CorrespondenceTimeline';
import ClientSendPrompt from './ClientSendPrompt';
import { Booking, BookingEditor, BookingFollowUp } from './StudioBookings';
import NotifyChannelPrompt from './NotifyChannelPrompt';
import ReplyReach, { SentReply } from './ReplyReach';
import ShootRequestEditor from './ShootRequestEditor';
import JobHistory from './JobHistory';
import StudioQuoteRecord, { type QuoteEditorHandle } from './StudioQuoteRecord';
import {
  buildStudioWhatsAppUrl,
  reminderWhatsAppText,
} from '../../utils/studioNotifyChannels';
import {
  composePhoneE164,
  DEFAULT_COUNTRY_DIAL,
  splitDialAndNational,
} from '../../utils/countryCodes';
import CountryCodeSelect from './CountryCodeSelect';
import { toShootTab, type ClientTab, type ShootTab } from './shootTab';

export type { ClientTab, ShootTab } from './shootTab';
export { toShootTab } from './shootTab';

interface PaymentSummary {
  lockedMinor: number;
  outstandingMinor: number;
  receivedMinor?: number;
  currentlyDueMinor?: number;
  depositDueMinor?: number;
  depositExpectedMinor?: number;
  depositReceivedMinor?: number;
  payments: Array<{
    id: string;
    type: string;
    status: string;
    verifiedMinor: number;
    expectedMinor?: number;
    note?: string;
    paidAt?: string | null;
    createdAt?: string;
    reference?: string | null;
    source?: string | null;
  }>;
  arrangement?: { id: string; status?: string; amountMinor?: number; note?: string; createdAt?: string } | null;
}

interface StudioEnquiry {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  source: string;
  shootType?: string;
  preferredDate?: string;
  notes?: string;
  receivedAt: string;
  clientKey: string;
  linkedQuoteId?: string | null;
}

/** The whole life of a job. Deposit is a mark, not a gate. Finish shoot is the step into editing. */
const FLOW_STEPS = [
  { id: 'enquiry', label: 'Client details' },
  { id: 'quoted', label: 'Quoted' },
  { id: 'accepted', label: 'Accepted' },
  { id: 'deposit', label: 'Deposit paid' },
  { id: 'confirm', label: 'Confirm shoot' },
  { id: 'finish', label: 'Finish shoot' },
  { id: 'editing', label: 'Editing' },
  { id: 'delivered', label: 'Delivery' },
  { id: 'closed', label: 'Closed' },
] as const;

type FlowStepId = typeof FLOW_STEPS[number]['id'];

const FLOW_RANK: FlowStepId[] = FLOW_STEPS.map((step) => step.id);

/**
 * What belongs under each progress stage. Messages sit in the free workspace for now
 * (placement still undecided). History is the closed-job audit, not a stage tab everywhere.
 */
type StageWorkspace = { tabs: ShootTab[]; defaultTab?: ShootTab } | { mode: 'accept' } | { mode: 'wrap' };

const STAGE_WORKSPACE: Record<FlowStepId, StageWorkspace> = {
  enquiry: { tabs: ['details'], defaultTab: 'details' },
  quoted: { tabs: ['money'], defaultTab: 'money' },
  accepted: { mode: 'accept' },
  deposit: { tabs: ['money'], defaultTab: 'money' },
  confirm: { tabs: ['booking'], defaultTab: 'booking' },
  finish: { mode: 'wrap' },
  editing: { mode: 'wrap' },
  delivered: { mode: 'wrap' },
  closed: { mode: 'wrap' },
};

function isHttpLink(value: string) {
  const text = value.trim();
  if (!text) return false;
  try {
    const parsed = new URL(text);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch (_) {
    return false;
  }
}

function shortDate(value?: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!match) return value || '';
  return new Intl.DateTimeFormat('en-ZA', { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .format(new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))));
}

function shortShootStatus(status?: string) {
  const value = String(status || '').trim().toLowerCase();
  if (value === 'issued') return 'Quoted';
  if (value === 'accepted') return 'Accepted';
  if (value === 'draft') return 'Draft';
  if (value === 'declined') return 'Declined';
  if (value === 'expired') return 'Expired';
  if (value === 'cancelled') return 'Cancelled';
  if (value === 'superseded') return 'Old';
  return status || 'Shoot';
}

function shortShootType(value?: string) {
  const label = clientFacingShootType(value);
  if (!label) return '';
  return label.length > 18 ? `${label.slice(0, 16)}…` : label;
}

function otherShootLabel(item: { preferredDate?: string; shootType?: string; status?: string; quoteNumber?: string }) {
  const date = shortDate(item.preferredDate) || 'TBC';
  const type = shortShootType(item.shootType) || 'Shoot';
  const status = shortShootStatus(item.status);
  return `${date} · ${type} · ${status}`;
}

function placeLine(location?: { name?: string; address?: string }) {
  const name = location?.name?.trim() || '';
  const address = location?.address?.trim() || '';
  if (name && address && name !== address) return `${name}, ${address}`;
  return name || address;
}

function EnquiryRecord({ enquiries }: { enquiries: StudioEnquiry[] }) {
  if (enquiries.length === 0) {
    return (
      <section className="studio-panel enquiry-record" data-testid="enquiry-record" aria-label="Client details">
        <h3 className="studio-section-label">Client details</h3>
        <p className="studio-note">No separate enquiry was recorded for this job.</p>
      </section>
    );
  }
  return (
    <section className="studio-panel enquiry-record" data-testid="enquiry-record" aria-label="Client details">
      <h3 className="studio-section-label">Client details</h3>
      {enquiries.map((item) => {
        const dateInMind = formatPreferredDate(item.preferredDate);
        const note = item.notes?.trim() || '';
        return (
          <div key={item.id} data-testid={`enquiry-record-${item.id}`}>
            <dl className="studio-summary-list">
              {item.receivedAt ? <div><dt>Received</dt><dd>{new Date(item.receivedAt).toLocaleString('en-ZA')}</dd></div> : null}
              {item.source ? <div><dt>Came in via</dt><dd>{item.source}</dd></div> : null}
              {item.shootType ? <div><dt>Asked for</dt><dd>{item.shootType}</dd></div> : null}
              {dateInMind ? <div><dt>Date in mind</dt><dd>{dateInMind}</dd></div> : null}
              {note ? <div><dt>Note</dt><dd className="enquiry-notes">{note}</dd></div> : null}
            </dl>
          </div>
        );
      })}
    </section>
  );
}

export interface ShootFileProps {
  quoteId?: string;
  enquiryId?: string;
  initialTab?: ShootTab | ClientTab;
  backLabel?: string;
  onBack: () => void;
  onOpenCalendar?: () => void;
  onOpenBuilder?: (quote?: StoredQuote, returnClientKey?: string, editIssued?: boolean) => void;
  /** Open another shoot for the same client. */
  onOpenShoot?: (quoteId: string, tab?: ShootTab) => void;
  onTabChange?: (tab: ShootTab) => void;
  onPaymentsChanged?: () => void;
  /** Ask before the shoot file is left while the quotation form is unsaved. */
  onLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
}

const ShootFile: React.FC<ShootFileProps> = ({
  quoteId: quoteIdProp = '',
  enquiryId: enquiryIdProp = '',
  initialTab,
  backLabel = 'Back',
  onBack,
  onOpenCalendar,
  onOpenBuilder,
  onOpenShoot,
  onTabChange,
  onPaymentsChanged,
  onLeaveGuard,
}) => {
  const [tab, setTabState] = useState<ShootTab>(toShootTab(initialTab));
  const requestLeaveRef = useRef<(proceed: () => void) => void>((proceed) => proceed());
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const stageViewRef = useRef<FlowStepId | null>(null);
  const setTab = (next: ShootTab) => {
    const go = () => {
      setStageView(null);
      setTabState(next);
      onTabChange?.(next);
    };
    if (stageViewRef.current === 'quoted') requestLeaveRef.current(go);
    else go();
  };

  const [quote, setQuote] = useState<StoredQuote | null>(null);
  const [relatedQuotes, setRelatedQuotes] = useState<Array<{
    id: string;
    quoteNumber?: string;
    status?: string;
    shootType?: string;
    preferredDate?: string;
    deletedAt?: string | null;
  }>>([]);
  const [enquiries, setEnquiries] = useState<StudioEnquiry[]>([]);
  const [summary, setSummary] = useState<PaymentSummary | undefined>();
  const [booking, setBooking] = useState<Booking | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [conversationMeta, setConversationMeta] = useState<{ preference?: string; clientLink?: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactCountryCode, setContactCountryCode] = useState(DEFAULT_COUNTRY_DIAL);
  const contactPhoneE164 = composePhoneE164(contactCountryCode, contactPhone);
  const [editingContact, setEditingContact] = useState(false);
  const [company, setCompany] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [clientKey, setClientKey] = useState('');
  const [reminders, setReminders] = useState<Array<{
    id: string;
    category: string;
    label: string;
    quoteId: string;
    reason: string;
    noticeType: string;
    link?: string;
  }>>([]);
  const [sentReply, setSentReply] = useState<SentReply | null>(null);
  const sendingReply = useRef(false);
  const [sendingReplyUi, setSendingReplyUi] = useState(false);
  const [pendingReminder, setPendingReminder] = useState<{
    id: string;
    category: string;
    label: string;
    quoteId: string;
    reason: string;
    noticeType: string;
    link?: string;
  } | null>(null);
  const [enquiryOnly, setEnquiryOnly] = useState<StudioEnquiry | null>(null);
  /** Which progress stage is open in the main panel. Null keeps the Money / Messages workspace. */
  const [stageView, setStageView] = useState<FlowStepId | null>(null);
  stageViewRef.current = stageView;
  const quoteEditorRef = useRef<QuoteEditorHandle | null>(null);
  const [quoteEditor, setQuoteEditor] = useState<QuoteEditorHandle | null>(null);
  const [leavePrompt, setLeavePrompt] = useState<(() => void) | null>(null);
  const [leaveBusy, setLeaveBusy] = useState(false);
  const [leaveError, setLeaveError] = useState('');
  quoteEditorRef.current = quoteEditor;
  requestLeaveRef.current = (proceed) => {
    if (!quoteEditorRef.current?.dirty) {
      proceed();
      return;
    }
    setLeaveError('');
    setLeavePrompt(() => proceed);
  };
  const [confirmDeleteEnquiry, setConfirmDeleteEnquiry] = useState(false);
  const [deleteEnquiryBusy, setDeleteEnquiryBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [emailAcceptanceNote, setEmailAcceptanceNote] = useState('');
  const [recordingAcceptance, setRecordingAcceptance] = useState(false);
  const [followUp, setFollowUp] = useState<BookingFollowUp | null>(null);
  const [deliveryLinkCopied, setDeliveryLinkCopied] = useState(false);
  const [receiptAsk, setReceiptAsk] = useState(0);
  const [reviewRequest, setReviewRequest] = useState<{
    reviewRequestSentAt?: string;
    lastSentAt?: string;
    reminderSentAt?: string;
    reviewReceived?: boolean;
    optedOut?: boolean;
    configured?: boolean;
    canSend?: boolean;
  } | null>(null);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [completionNote, setCompletionNote] = useState('');
  const [completeBusy, setCompleteBusy] = useState(false);
  const [deliveryDue, setDeliveryDue] = useState('');
  const [deliveryLink, setDeliveryLink] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [deliveryMessage, setDeliveryMessage] = useState('');
  const [deliverBusy, setDeliverBusy] = useState(false);
  const [mailAsk, setMailAsk] = useState<{ title: string; detail: string; send: () => void; skip: () => void } | null>(null);
  const [closeNote, setCloseNote] = useState('');
  const [closeBusy, setCloseBusy] = useState(false);
  const [uncloseEditing, setUncloseEditing] = useState(false);
  const [reopenDate, setReopenDate] = useState('');
  const [reopenNote, setReopenNote] = useState('');

  useEffect(() => {
    setTabState(toShootTab(initialTab));
  }, [initialTab, quoteIdProp, enquiryIdProp]);

  const latestEnquiry = enquiries[0] || enquiryOnly;
  const journey = deriveClientJourney(quote, summary, booking, latestEnquiry || undefined);

  const loadReminders = async (id?: string) => {
    const query = id ? `?quoteId=${encodeURIComponent(id)}` : '';
    const response = await fetch(`${API_BASE_URL}/studio/reminders${query}`);
    if (!response.ok) {
      setReminders([]);
      return;
    }
    const body = await response.json();
    const rows = Array.isArray(body.suggestions) ? body.suggestions : [];
    setReminders(id ? rows.filter((row: { quoteId: string }) => row.quoteId === id) : rows);
  };

  const loadConversation = async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/studio/messages/${id}`);
    if (!response.ok) {
      setTimeline([]);
      return;
    }
    const body = await response.json();
    setTimeline(Array.isArray(body.timeline) ? body.timeline as TimelineEntry[] : []);
    setConversationMeta({ preference: body.preference, clientLink: body.clientLink });
  };

  const loadPaymentSummary = async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/studio/quotes/${id}/summary`);
    if (!response.ok) {
      setSummary(undefined);
      return;
    }
    const summary = await response.json();
    if (!summary || summary.error) {
      setSummary(undefined);
      return;
    }
    setSummary(summary);
  };

  const loadReviewRequest = async (id: string) => {
    const response = await fetch(`${API_BASE_URL}/studio/quotes/${id}/review-request`);
    if (!response.ok) {
      setReviewRequest(null);
      return;
    }
    const body = await response.json();
    setReviewRequest(body);
  };

  const formatReviewWhen = (value?: string) => {
    if (!value) return '';
    const ms = Date.parse(value);
    if (!Number.isFinite(ms)) return value;
    return new Intl.DateTimeFormat('en-ZA', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(ms));
  };

  const sendReviewRequest = async () => {
    if (!quote?.id) return;
    setReviewBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/review-request/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not send the review request.');
      setReviewRequest((current) => ({ ...(current || {}), ...body }));
      setMessage(body.duplicate ? 'A review request was already sent for this shoot.' : 'Review request sent.');
      await loadReviewRequest(quote.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the review request.');
    } finally {
      setReviewBusy(false);
    }
  };

  const setReviewReceived = async (next: boolean) => {
    if (!quote?.id) return;
    setReviewBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/review-request`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewReceived: next }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'Could not update review status.');
      setReviewRequest((current) => ({ ...(current || {}), ...body }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update review status.');
    } finally {
      setReviewBusy(false);
    }
  };

  const markJobComplete = async () => {
    if (!quote?.id) return;
    setCompleteBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: completionNote.trim(), deliveryDueOn: deliveryDue || undefined }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The shoot could not be marked done.');
      if (body.booking) mergeBooking(body.booking);
      setCompletionNote('');
      setDeliveryDue('');
      setMessage(body.duplicate
        ? 'This shoot was already marked done.'
        : booking?.completedAt
          ? `Editing date saved${body.booking?.deliveryDueOn ? `. The photographs are due by ${shortDate(body.booking.deliveryDueOn)}` : ''}.`
          : 'Shoot marked done. The job is in editing — add the delivery link when the photographs are ready.');
      setStageView('editing');
      await Promise.all([
        loadConversation(quote.id),
        loadPaymentSummary(quote.id),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The shoot could not be marked done.');
    } finally {
      setCompleteBusy(false);
    }
  };

  const mergeBooking = (saved: Partial<Booking>) => {
    if (!quote) return;
    setBooking((current) => ({
      ...(current || {
        quoteId: quote.id,
        quoteNumber: quote.quoteNumber,
        clientName: contactName || quote.client?.name || 'Client',
        shootType: quote.shootType || '',
        timezone: 'Africa/Johannesburg',
      }),
      ...saved,
    }));
  };

  const deliverPhotos = async (channel: 'email' | 'whatsapp' | 'none') => {
    if (!quote?.id || deliverBusy) return;
    const link = deliveryLink.trim();
    if (link && !isHttpLink(link)) {
      setError('The delivery link must start with https:// (a WeTransfer link, for example).');
      return;
    }
    setDeliverBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/deliver`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          link,
          note: deliveryNote.trim(),
          notify: channel !== 'none',
          channel: channel === 'whatsapp' ? 'whatsapp' : 'email',
          personalMessage: deliveryMessage.trim(),
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The delivery could not be recorded.');
      if (body.booking) mergeBooking(body.booking);
      if (channel === 'whatsapp' && contactPhoneE164) {
        const text = [
          `Hi ${(contactName || quote.client?.name || '').split(' ')[0] || 'there'}, this is Carmen. Your photographs are ready.`,
          deliveryMessage.trim(),
          link ? `Download them here: ${link}` : '',
          'Please save a copy somewhere safe — the link does not stay live forever.',
        ].filter(Boolean).join('\n\n');
        window.open(buildStudioWhatsAppUrl(contactPhoneE164, text), '_blank', 'noopener,noreferrer');
      }
      setDeliveryLink('');
      setDeliveryNote('');
      setDeliveryMessage('');
      const paidNow = Boolean(body.paidInFull);
      setMessage(body.duplicate
        ? 'The photographs were already marked delivered.'
        : `Photographs delivered${channel === 'email' ? ' and the client emailed' : channel === 'whatsapp' ? ' — WhatsApp opened for you to send' : ''}.${paidNow ? ' Paid in full, so the Google review thank-you follows automatically.' : ' The review email waits for the final balance.'}`);
      await Promise.all([
        loadReviewRequest(quote.id),
        loadConversation(quote.id),
        loadPaymentSummary(quote.id),
      ]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The delivery could not be recorded.');
    } finally {
      setDeliverBusy(false);
    }
  };

  const closeJob = async () => {
    if (!quote?.id || closeBusy) return;
    setCloseBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/close`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: closeNote.trim() }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The job could not be closed.');
      if (body.booking) mergeBooking(body.booking);
      setCloseNote('');
      setUncloseEditing(false);
      setStageView('closed');
      setMessage(body.duplicate ? 'This job is already closed.' : 'Job closed. It leaves Today and the pipeline; the history stays here.');
      await loadConversation(quote.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The job could not be closed.');
    } finally {
      setCloseBusy(false);
    }
  };

  const beginUnclose = () => {
    setReopenDate(booking?.shootDate || booking?.proposedShootDate || quote?.preferredDate || '');
    setReopenNote('');
    setUncloseEditing(true);
  };

  const reopenJob = async () => {
    if (!quote?.id || closeBusy) return;
    setCloseBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/reopen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shootDate: reopenDate.trim() || undefined,
          note: reopenNote.trim() || undefined,
        }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The job could not be reopened.');
      if (body.booking) mergeBooking({ ...body.booking, closedAt: '', closeNote: '' });
      setUncloseEditing(false);
      setReopenNote('');
      setStageView(null);
      setMessage(body.duplicate ? 'This job is already open.' : 'Job unclosed.');
      if (quote.id) await loadConversation(quote.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The job could not be reopened.');
    } finally {
      setCloseBusy(false);
    }
  };

  const scrollToProgress = () => {
    window.setTimeout(() => {
      document.querySelector<HTMLElement>('[data-testid="job-wrap-up"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      setError('');
      setMessage('');
      setSentReply(null);
      setFollowUp(null);
      setEnquiryOnly(null);
      setStageView(null);
      setUncloseEditing(false);
      setReopenDate('');
      setReopenNote('');
      setQuote(null);
      try {
        if (quoteIdProp) {
          const response = await fetch(`${API_BASE_URL}/studio/quotes/${quoteIdProp}/file`);
          if (!response.ok) throw new Error('That shoot could not be loaded.');
          const body = await response.json();
          const loaded = body.quote as StoredQuote;
          if (!active || !loaded) return;
          setQuote(loaded);
          const person = body.client || {};
          const name = person.name || loaded.client?.name || 'Client';
          setClientKey(person.key || clientKeyForQuote(loaded));
          setContactName(name);
          setContactEmail(person.email || loaded.client?.email || '');
          {
            const storedDial = person.countryCode || loaded.client?.countryCode || DEFAULT_COUNTRY_DIAL;
            const parts = splitDialAndNational(
              person.phoneE164 || loaded.client?.phoneE164 || '',
              storedDial,
            );
            setContactCountryCode(parts.dial);
            setContactPhone(parts.national);
          }
          setCompany(person.company || loaded.client?.company || '');
          setBillingAddress(loaded.client?.billingAddress || '');
          setRelatedQuotes(Array.isArray(body.relatedQuotes) ? body.relatedQuotes : []);
          setEnquiries(Array.isArray(body.enquiries) ? body.enquiries : []);
          setBooking(body.booking ? {
            quoteId: loaded.id,
            quoteNumber: loaded.quoteNumber,
            clientName: name,
            shootType: loaded.shootType || '',
            timezone: 'Africa/Johannesburg',
            ...body.booking,
          } : {
            quoteId: loaded.id,
            quoteNumber: loaded.quoteNumber,
            clientName: name,
            shootType: loaded.shootType || '',
            timezone: 'Africa/Johannesburg',
          });
          setTimeline(Array.isArray(body.timeline) ? body.timeline as TimelineEntry[] : []);
          setConversationMeta({ preference: body.preference, clientLink: body.clientLink });
          setSummary(body.summary || undefined);
          setReminders(Array.isArray(body.reminders) ? body.reminders : []);
          await loadReviewRequest(loaded.id);
        } else if (enquiryIdProp) {
          const response = await fetch(`${API_BASE_URL}/studio/enquiries/${enquiryIdProp}`);
          if (!response.ok) throw new Error('That enquiry could not be loaded.');
          const enquiry = await response.json() as StudioEnquiry;
          if (!active) return;
          setEnquiryOnly(enquiry);
          setEnquiries([enquiry]);
          setClientKey(enquiry.clientKey);
          setContactName(enquiry.name);
          setContactEmail(enquiry.email || '');
          {
            const parts = splitDialAndNational(enquiry.phone || '', DEFAULT_COUNTRY_DIAL);
            setContactCountryCode(parts.dial);
            setContactPhone(parts.national);
          }
          setBillingAddress('');
          setRelatedQuotes([]);
          setBooking(null);
          setTimeline([]);
          setSummary(undefined);
          setReminders([]);
          setReviewRequest(null);
        } else {
          throw new Error('No shoot or enquiry was specified.');
        }
      } catch (loadError) {
        if (active) setError(loadError instanceof Error ? loadError.message : 'The shoot could not be loaded.');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [quoteIdProp, enquiryIdProp]);

  const prepareFromEnquiry = (item: StudioEnquiry) => {
    onOpenBuilder?.({
      id: '',
      status: 'draft',
      quoteNumber: 'New',
      version: 0,
      createdAt: '',
      updatedAt: '',
      snapshot: { currency: 'R', quoteTotalMinor: 0, depositMinor: 0, balanceMinor: 0, lines: [] } as unknown as StoredQuote['snapshot'],
      shootType: item.shootType || '',
      preferredDate: item.preferredDate || '',
      client: {
        name: item.name,
        email: item.email || '',
        phoneE164: item.phone || '',
        preference: 'either',
      },
      draft: {
        clientName: item.name,
        recipientEmail: item.email || '',
        clientPhone: item.phone || '',
        shootType: item.shootType || '',
        preferredDate: item.preferredDate || '',
      },
    } as StoredQuote, item.clientKey);
    if (item.id) {
      sessionStorage.setItem('studioPendingEnquiryLink', JSON.stringify({ enquiryId: item.id, clientKey: item.clientKey }));
    }
  };

  // An enquiry that is going no further: remove it and return to the list.
  const deleteEnquiry = async () => {
    const target = enquiryOnly || latestEnquiry;
    if (!target || quote || deleteEnquiryBusy) return;
    setDeleteEnquiryBusy(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/enquiries/${encodeURIComponent(target.id)}`, { method: 'DELETE' });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.error || 'The enquiry could not be removed.');
      onBack();
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'The enquiry could not be removed.');
      setConfirmDeleteEnquiry(false);
    } finally {
      setDeleteEnquiryBusy(false);
    }
  };

  const sendReply = async () => {
    if (!quote || !reply.trim() || sendingReply.current) return;
    sendingReply.current = true;
    setSendingReplyUi(true);
    const clientRequestId = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `reply-${Date.now()}`;
    try {
      const response = await fetch(`${API_BASE_URL}/studio/messages/${quote.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: reply.trim(), clientRequestId }),
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(body.error || 'The reply could not be saved.');
        return;
      }
      setSentReply({
        text: reply.trim(),
        delivery: body.delivery,
        destination: body.destination,
        providerAccepted: body.providerAccepted === true,
      });
      setReply('');
      await loadConversation(quote.id);
    } finally {
      sendingReply.current = false;
      setSendingReplyUi(false);
    }
  };

  const actOnReminder = async (action: 'send' | 'snooze' | 'dismiss', row: {
    quoteId: string;
    category: string;
    label?: string;
    id?: string;
    reason?: string;
    noticeType?: string;
  }) => {
    if (action === 'send') {
      setPendingReminder({
        id: row.id || `${row.category}:${row.quoteId}`,
        category: row.category,
        label: row.label || 'Reminder',
        quoteId: row.quoteId,
        reason: row.reason || '',
        noticeType: row.noticeType || '',
      });
      return;
    }
    const response = await fetch(`${API_BASE_URL}/studio/reminders/${action}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quoteId: row.quoteId, category: row.category }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body.error || 'The reminder action could not be completed.');
      return;
    }
    setMessage(action === 'snooze' ? 'Reminder snoozed for 24 hours.' : 'Reminder dismissed.');
    await loadReminders(quote?.id);
  };

  const sendReminderViaApi = async (row: { quoteId: string; category: string }, skipEmail = false) => {
    const response = await fetch(`${API_BASE_URL}/studio/reminders/send`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ quoteId: row.quoteId, category: row.category, skipEmail }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(body.error || 'The reminder could not be recorded.');
    }
    return { status: body.notice?.status as string | undefined, error: body.notice?.lastError as string | undefined };
  };

  const saveNote = async (channel: 'internal' | 'whatsapp-note', text: string, clear: () => void) => {
    if (!quote || !text.trim()) return;
    const response = await fetch(`${API_BASE_URL}/studio/messages/${quote.id}/note`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: text.trim(), channel }),
    });
    if (!response.ok) {
      setError('The note could not be saved.');
      return;
    }
    clear();
    setMessage(channel === 'whatsapp-note'
      ? 'WhatsApp note saved in studio only. WhatsApp is not synchronised.'
      : 'Private note saved.');
    await loadConversation(quote.id);
  };

  const saveContact = async () => {
    if (!quote) return;
    const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/contact`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: contactName,
        email: contactEmail,
        countryCode: contactCountryCode,
        phone: contactPhone,
        company,
        billingAddress,
        preference: quote.client?.preference || 'either',
      }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
      setError(body.error || 'Contact could not be saved.');
      return;
    }
    setMessage('Contact details saved on this quotation.');
    setEditingContact(false);
    const refreshed = await fetch(`${API_BASE_URL}/quotes/${quote.id}`);
    if (refreshed.ok) setQuote(await refreshed.json() as StoredQuote);
  };

  const packageName = quote?.snapshot?.lines?.find((line) => line.kind === 'package')?.name
    || clientFacingShootType(quote?.shootType)
    || latestEnquiry?.shootType
    || 'Quotation';

  const money = (minor?: number, currency = 'R') => formatQuoteMoney(minor || 0, currency);

  const canPrepare = !quote && Boolean(latestEnquiry && onOpenBuilder);
  const canEditDraft = quote?.status === 'draft';
  const productionStep = journey.nextButton === 'Finish shoot' || journey.nextButton === 'Deliver photos' || journey.nextButton === 'Close job';
  const showNextButton = Boolean(journey.nextButton && journey.nextTab)
    && (productionStep || journey.nextTab !== tab)
    && (journey.nextTab !== 'builder' || canPrepare || canEditDraft);

  const runNextStep = () => {
    if (journey.nextButton === 'Confirm shoot') {
      setTab('booking');
      confirmCurrentShoot();
      return;
    }
    if (journey.nextButton === 'Finish shoot') {
      scrollToProgress();
      return;
    }
    if (journey.nextButton === 'Deliver photos') {
      scrollToProgress();
      window.setTimeout(() => {
        document.querySelector<HTMLInputElement>('[data-testid="delivery-link"]')?.focus();
      }, 80);
      return;
    }
    if (journey.nextButton === 'Close job') {
      scrollToProgress();
      return;
    }
    if (journey.nextTab === 'builder') {
      if (canEditDraft) {
        setStageView('quoted');
        setTab('money');
      } else if (canPrepare && latestEnquiry) {
        prepareFromEnquiry(latestEnquiry);
      }
      return;
    }
    if (journey.nextButton === 'Record payment' || journey.nextTab === 'money') {
      setReceiptAsk((count) => count + 1);
      setStageView('deposit');
      setTab('money');
      return;
    }
    if (journey.nextTab === 'conversation' || journey.nextTab === 'booking') {
      setTab(journey.nextTab);
    }
  };

  const paidInFull = Boolean(
    summary
    && Number(summary.outstandingMinor) === 0
    && Number(summary.receivedMinor || 0) > 0,
  );
  const jobComplete = Boolean(booking?.completedAt);
  const jobDelivered = Boolean(booking?.deliveredAt);
  const jobClosed = Boolean(booking?.closedAt);
  const bookingSaved = Boolean(booking?.quoteId && (booking.shootDate || booking.proposedShootDate || booking.dateConfirmed || booking.completedAt));
  const progressFocus: 'finish' | 'editing' | 'delivery' | 'closed' = jobClosed
    ? 'closed'
    : jobDelivered
      ? 'delivery'
      : jobComplete
        ? 'editing'
        : 'finish';
  /** Same window as showProgress later: finish / editing / closed — not the Delivery stage. */
  const freeWrapOpen = jobClosed
    || (jobComplete && !jobDelivered)
    || (Boolean(booking?.dateConfirmed && booking?.shootDate) && !jobComplete);
  /** Prefer the opened stage so Finish never shows Delivery / Google review. */
  const wrapFocus: 'finish' | 'editing' | 'delivery' | 'closed' | null = stageView === 'finish'
    ? 'finish'
    : stageView === 'editing'
      ? 'editing'
      : stageView === 'delivered'
        ? 'delivery'
        : stageView === 'closed'
          ? 'closed'
          : (stageView == null && freeWrapOpen ? progressFocus : null);

  const activeDeliveryLink = deliveryLink.trim() || booking?.deliveryLink || '';
  const copyDeliveryLink = async () => {
    if (!activeDeliveryLink) return;
    try {
      await navigator.clipboard.writeText(activeDeliveryLink);
      setDeliveryLinkCopied(true);
      window.setTimeout(() => setDeliveryLinkCopied(false), 2000);
    } catch (_) {
      setError('The delivery link could not be copied.');
    }
  };

  // Finish → Editing (built-in photos-delivered: WeTransfer link + ready message + email/WhatsApp) → Delivery → Closed.
  const jobCompletePanel = quote?.status === 'accepted' && wrapFocus ? (
    <div className={`studio-panel studio-wrap-up is-${wrapFocus}`} data-testid="job-wrap-up">
      <h3 className="studio-section-label">
        {wrapFocus === 'finish' ? 'Finish shoot' : wrapFocus === 'editing' ? 'Editing' : wrapFocus === 'delivery' ? 'Delivery' : 'Closed'}
      </h3>

      {wrapFocus === 'finish' ? (
        <div className="studio-stage-fields" data-testid="finish-shoot-only">
          <p className="studio-note">
            Close out the shoot day. That moves the job into editing — nothing else on this step.
          </p>
          {!bookingSaved ? (
            <p className="studio-note">Confirm the shoot date first.</p>
          ) : null}
          <button
            type="button"
            className="studio-primary"
            onClick={() => markJobComplete()}
            disabled={completeBusy || !bookingSaved}
            data-testid="mark-job-complete"
          >
            {completeBusy ? 'Saving…' : 'Finish shoot'}
          </button>
        </div>
      ) : null}

      {wrapFocus === 'editing' ? (
        <div className="studio-stage-fields studio-delivery-form" data-testid="delivery-comms-form">
          <p className={`studio-note${journey.deliveryOverdue ? ' studio-overdue' : ''}`} data-testid="delivery-countdown">
            {journey.deliveryLine ? `${journey.deliveryLine}.` : 'Set when the photographs are due, then send the delivery.'}
            {!paidInFull && summary ? ` ${money(summary.outstandingMinor)} is still outstanding.` : ''}
          </p>

          <label>
            Photographs due by
            <input
              type="date"
              value={deliveryDue || booking?.deliveryDueOn || ''}
              onChange={(event) => setDeliveryDue(event.target.value)}
              aria-label="Delivery due date"
              data-testid="delivery-due"
            />
          </label>
          {deliveryDue && deliveryDue !== (booking?.deliveryDueOn || '') ? (
            <button
              type="button"
              className="studio-secondary"
              onClick={() => markJobComplete()}
              disabled={completeBusy}
            >
              {completeBusy ? 'Saving…' : 'Save due date'}
            </button>
          ) : null}

          <fieldset className="studio-delivery-comms" data-testid="photos-delivered-form">
            <legend className="studio-section-label">Delivery</legend>
            <p className="studio-note">
              Built-in hand-over: paste the WeTransfer (or gallery) link and a short ready message. Email uses the Photographs delivered template; WhatsApp opens with the same link and message.
            </p>
            <label>
              WeTransfer or gallery link
              <input
                type="url"
                inputMode="url"
                placeholder="https://we.tl/…"
                value={deliveryLink}
                onChange={(event) => setDeliveryLink(event.target.value)}
                aria-label="Delivery link"
                data-testid="delivery-link"
              />
            </label>
            <label>
              Message to the client
              <textarea
                value={deliveryMessage}
                onChange={(event) => setDeliveryMessage(event.target.value)}
                maxLength={1000}
                rows={4}
                placeholder="Your photographs are ready — here's your download link."
                aria-label="Ready message for the client"
                data-testid="delivery-message"
              />
            </label>
            <label>
              Internal note
              <input
                value={deliveryNote}
                onChange={(event) => setDeliveryNote(event.target.value)}
                maxLength={2000}
                aria-label="Internal delivery note"
                data-testid="delivery-note"
              />
            </label>
            <div className="studio-toolbar" style={{ flexWrap: 'wrap' }}>
              <button
                type="button"
                className="studio-primary"
                onClick={() => {
                  if (!contactEmail) {
                    void deliverPhotos('none');
                    return;
                  }
                  setMailAsk({
                    title: 'Email the client?',
                    detail: `The photographs are ready${contactName ? ` for ${contactName}` : ''}. Send the delivery email, or mark them delivered without emailing.`,
                    send: () => { setMailAsk(null); void deliverPhotos('email'); },
                    skip: () => { setMailAsk(null); void deliverPhotos('none'); },
                  });
                }}
                disabled={deliverBusy}
                data-testid="deliver-photos"
              >
                {deliverBusy ? 'Sending…' : 'Mark delivered'}
              </button>
              {contactPhoneE164 ? (
                <button
                  type="button"
                  className="studio-secondary"
                  onClick={() => deliverPhotos('whatsapp')}
                  disabled={deliverBusy}
                  data-testid="deliver-photos-whatsapp"
                >
                  {deliverBusy ? 'Preparing…' : 'Send via WhatsApp'}
                </button>
              ) : null}
              {activeDeliveryLink ? (
                <>
                  <button type="button" className="studio-text-action" onClick={copyDeliveryLink} data-testid="copy-delivery-link">
                    {deliveryLinkCopied ? 'Link copied' : 'Copy link'}
                  </button>
                  <a
                    className="studio-text-action"
                    href={activeDeliveryLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="open-delivery-link"
                  >
                    Open transfer link
                  </a>
                </>
              ) : null}
            </div>
          </fieldset>
        </div>
      ) : null}

      {wrapFocus === 'delivery' ? (
        <div className="studio-stage-fields">
          {booking?.deliveryLink ? (
            <p className="studio-note">
              Delivery link:{' '}
              <a href={booking.deliveryLink} target="_blank" rel="noopener noreferrer">{booking.deliveryLink}</a>
              {' · '}
              <button type="button" className="studio-text-action" onClick={copyDeliveryLink}>
                {deliveryLinkCopied ? 'Copied' : 'Copy link'}
              </button>
            </p>
          ) : (
            <p className="studio-note">Photographs marked delivered. No transfer link was saved on this job.</p>
          )}
          <p className="studio-note">
            {paidInFull
              ? 'The photographs are with the client. Close the job when you are done with it.'
              : 'A balance is still open. Record the rest under Deposit paid, then close.'}
          </p>
          <div className="studio-panel" data-testid="google-review-card" style={{ marginTop: '0.5rem' }}>
            <h3 className="studio-section-label">Google review</h3>
            {reviewRequest?.optedOut ? (
              <p className="studio-note">This client has opted out of review emails.</p>
            ) : reviewRequest?.reminderSentAt ? (
              <p className="studio-note">Reminded {formatReviewWhen(reviewRequest.reminderSentAt)}. Use Resend if you want another note.</p>
            ) : reviewRequest?.reviewRequestSentAt || reviewRequest?.lastSentAt ? (
              <p className="studio-note">
                Sent {formatReviewWhen(reviewRequest.reviewRequestSentAt || reviewRequest.lastSentAt)}.
                {reviewRequest.lastSentAt && reviewRequest.lastSentAt !== reviewRequest.reviewRequestSentAt
                  ? ` Last sent ${formatReviewWhen(reviewRequest.lastSentAt)}.`
                  : ''}
              </p>
            ) : (
              <p className="studio-note">
                {!paidInFull
                  ? 'Automatic send waits until paid in full. You can still send manually.'
                  : 'Ready to send — or it may already be on its way.'}
              </p>
            )}
            {!reviewRequest?.configured ? (
              <p className="studio-note">Set the Google review link under Studio → Reviews before sending.</p>
            ) : null}
            <div className="studio-toolbar">
              <button
                type="button"
                className="studio-primary"
                onClick={sendReviewRequest}
                disabled={reviewBusy || reviewRequest?.optedOut || reviewRequest?.configured === false}
                data-testid="send-review-request"
              >
                {reviewBusy
                  ? 'Sending…'
                  : (reviewRequest?.reviewRequestSentAt || reviewRequest?.lastSentAt ? 'Resend review request' : 'Send review request')}
              </button>
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(reviewRequest?.reviewReceived)}
                  disabled={reviewBusy}
                  onChange={(event) => setReviewReceived(event.target.checked)}
                />
                {' '}Review received
              </label>
            </div>
          </div>
          <details className="studio-private-note">
            <summary>Add a closing note</summary>
            <label>
              Closing note
              <input
                value={closeNote}
                onChange={(event) => setCloseNote(event.target.value)}
                maxLength={2000}
                aria-label="Closing note"
              />
            </label>
          </details>
          <button type="button" className="studio-primary" onClick={closeJob} disabled={closeBusy} data-testid="close-job">
            {closeBusy ? 'Closing…' : 'Close'}
          </button>
        </div>
      ) : null}

      {wrapFocus === 'closed' ? (
        <div className="studio-stage-fields" data-testid="job-closed-panel">
          <p className="studio-note">
            <strong>Closed</strong>
            {booking?.closedAt ? ` · ${formatReviewWhen(booking.closedAt)}` : ''}
            {booking?.closeNote ? `. ${booking.closeNote}` : ''}
          </p>
          {booking?.deliveryLink ? (
            <p className="studio-note">
              Photographs:{' '}
              <a href={booking.deliveryLink} target="_blank" rel="noopener noreferrer">{booking.deliveryLink}</a>
            </p>
          ) : null}
          {!uncloseEditing ? (
            <div className="studio-toolbar">
              <button
                type="button"
                className="studio-secondary"
                onClick={beginUnclose}
                disabled={closeBusy}
                data-testid="unclose-job"
              >
                Unclose
              </button>
            </div>
          ) : (
            <div className="studio-contact-grid" data-testid="unclose-editor">
              <label>
                Shoot date
                <input
                  type="date"
                  aria-label="Shoot date"
                  value={reopenDate}
                  onChange={(event) => setReopenDate(event.target.value)}
                  data-testid="unclose-date"
                />
              </label>
              <label className="studio-billing-address">
                Note
                <textarea
                  aria-label="Unclose note"
                  rows={3}
                  maxLength={2000}
                  value={reopenNote}
                  onChange={(event) => setReopenNote(event.target.value)}
                  placeholder="Why this job is open again"
                  data-testid="unclose-note"
                />
              </label>
              <div className="studio-toolbar">
                <button
                  type="button"
                  className="studio-primary"
                  onClick={reopenJob}
                  disabled={closeBusy}
                  data-testid="confirm-unclose"
                >
                  {closeBusy ? 'Unclosing…' : 'Unclose'}
                </button>
                <button
                  type="button"
                  className="studio-secondary"
                  onClick={() => setUncloseEditing(false)}
                  disabled={closeBusy}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  ) : null;

  const pendingChange = Boolean(booking?.dateConfirmed && booking?.proposedShootDate && booking?.proposedStartTime);
  const confirmDate = pendingChange
    ? (booking?.proposedShootDate || '')
    : (booking?.shootDate || booking?.proposedShootDate || quote?.preferredDate || '');
  const confirmStart = (pendingChange
    ? (booking?.proposedStartTime || '')
    : (booking?.startTime || booking?.proposedStartTime || quote?.preferredTime || '')
  ).slice(0, 5);
  const confirmEnd = (pendingChange
    ? (booking?.proposedEndTime || '')
    : (booking?.endTime || booking?.proposedEndTime || '')
  ).slice(0, 5);
  const confirmPlace = [
    booking?.locationName || quote?.preferredLocation?.name,
    booking?.address || quote?.preferredLocation?.address,
  ].filter(Boolean).join(', ');
  const quoteDays = quote && Array.isArray(quote.sessions) && quote.sessions.length > 1 ? quote.sessions : [];
  const savedDays = booking?.sessions && booking.sessions.length > 1 ? booking.sessions : [];
  const previewDays = Math.max(quoteDays.length, savedDays.length) > 1
    ? Array.from({ length: Math.max(quoteDays.length, savedDays.length) }, (_, index) => {
      const asked = quoteDays[index];
      const saved = savedDays[index];
      const place = asked?.location;
      return [
        saved?.label || asked?.label || `Day ${index + 1}`,
        formatPreferredDate(saved?.shootDate || asked?.date),
        [saved?.locationName || place?.name, saved?.address || place?.address].filter(Boolean).join(', '),
      ].filter(Boolean).join(' · ');
    }).join('   ')
    : '';
  const confirmPreview = previewDays || [formatPreferredDate(confirmDate), confirmStart ? (confirmEnd ? `${confirmStart}–${confirmEnd}` : confirmStart) : '', confirmPlace]
    .filter(Boolean)
    .join(' · ');

  const recordEmailAcceptance = async () => {
    if (!quote || recordingAcceptance || quote.status !== 'issued') return;
    const proceed = window.confirm('Record this quotation as accepted from the client\'s email? The total will be locked. No payment is recorded, and the client is not emailed.');
    if (!proceed) return;
    setRecordingAcceptance(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/accept-from-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ note: emailAcceptanceNote.trim() }),
      });
      const saved = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(saved.error || 'The acceptance could not be recorded.');
        return;
      }
      setQuote(saved as StoredQuote);
      setEmailAcceptanceNote('');
      setMessage('Acceptance recorded. The total is locked. Record the deposit when it arrives, or confirm the shoot from Quoted if you need the date locked first.');
      if (saved.id) {
        loadConversation(saved.id).catch(() => undefined);
        loadPaymentSummary(saved.id).catch(() => undefined);
      }
    } catch (_) {
      setError('The acceptance could not be recorded.');
    } finally {
      setRecordingAcceptance(false);
    }
  };

  const confirmCurrentShoot = async (options?: {
    shootDate?: string;
    startTime?: string;
    endTime?: string;
    fromQuoted?: boolean;
    notifyClient?: boolean;
  }) => {
    if (!quote || confirming) return;
    const shootDate = (options?.shootDate || confirmDate || '').trim();
    const startTime = (options?.startTime || confirmStart || '').trim() || '09:00';
    const endTime = (options?.endTime || confirmEnd || '').trim();
    const savedSessions = booking && Array.isArray(booking.sessions) ? booking.sessions : [];
    const quoteSessions = Array.isArray(quote.sessions) ? quote.sessions : [];
    const dayCount = Math.max(savedSessions.length, quoteSessions.length);
    const sessionsPayload = dayCount > 1
      ? Array.from({ length: dayCount }, (_, index) => {
        const saved = savedSessions[index];
        const asked = quoteSessions[index];
        const place = asked?.location || {};
        return {
          id: saved?.id || asked?.id || `session-${index + 1}`,
          label: saved?.label || asked?.label || `Day ${index + 1}`,
          date: saved?.shootDate || asked?.date || (index === 0 ? shootDate : ''),
          shootDate: saved?.shootDate || asked?.date || (index === 0 ? shootDate : ''),
          startTime: String(saved?.startTime || asked?.startTime || (index === 0 ? startTime : '')).slice(0, 5),
          durationMinutes: saved?.durationMinutes || asked?.durationMinutes || 0,
          locationName: saved?.locationName || place.name || '',
          address: saved?.address || place.address || '',
          mapsUrl: saved?.mapsUrl || place.mapsUrl || '',
          location: {
            name: saved?.locationName || place.name || '',
            address: saved?.address || place.address || '',
            mapsUrl: saved?.mapsUrl || place.mapsUrl || '',
          },
        };
      })
      : undefined;
    const header = sessionsPayload?.[0];
    const headerDate = header?.date || shootDate;
    if (!headerDate || (sessionsPayload && sessionsPayload.some((day) => !day.date || !day.startTime))) {
      if (options?.fromQuoted && !headerDate) {
        setError('Choose the confirmed shoot date.');
      } else {
        setTab('booking');
        setMessage(sessionsPayload
          ? 'Each day needs a date and a start time before the shoot is confirmed.'
          : 'Add a date and a start time, then confirm the shoot.');
      }
      return;
    }
    if (options?.notifyClient === undefined) {
      const changing = Boolean(pendingChange || booking?.dateConfirmed);
      setMailAsk({
        title: 'Email the client?',
        detail: changing
          ? 'Save this shoot change, and choose whether the client is emailed about it.'
          : 'Confirm this shoot, and choose whether the client is emailed.',
        send: () => { setMailAsk(null); void confirmCurrentShoot({ ...options, notifyClient: true }); },
        skip: () => { setMailAsk(null); void confirmCurrentShoot({ ...options, notifyClient: false }); },
      });
      return;
    }
    setConfirming(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/studio/quotes/${quote.id}/booking`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          shootDate: headerDate,
          startTime: header?.startTime || startTime,
          endTime,
          timezone: booking?.timezone || 'Africa/Johannesburg',
          locationName: header?.locationName || booking?.locationName || quote.preferredLocation?.name || '',
          address: header?.address || booking?.address || quote.preferredLocation?.address || '',
          mapsUrl: header?.mapsUrl || booking?.mapsUrl || quote.preferredLocation?.mapsUrl || '',
          sessions: sessionsPayload,
          dateConfirmed: true,
          applyConfirmed: Boolean(booking?.dateConfirmed),
          notifyClient: options?.notifyClient === true,
        }),
      });
      const saved = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(saved.error || 'The shoot could not be confirmed.');
        if (!options?.fromQuoted) setTab('booking');
        return;
      }
      const nextBooking = {
        ...(booking || {
          quoteId: quote.id,
          quoteNumber: quote.quoteNumber,
          clientName: contactName || quote.client?.name || 'Client',
          shootType: quote.shootType || '',
          timezone: 'Africa/Johannesburg',
        }),
        ...(saved.booking || {}),
      };
      setBooking(nextBooking);
      const notice = saved.notice || saved.booking?.clientNotice || null;
      const noticeLine = !notice
        ? (options?.notifyClient === false ? 'The client was not emailed.' : '')
        : notice.duplicate
          ? 'Client update already recorded for this booking change.'
          : notice.status === 'failed'
            ? 'Shoot saved, but the client email failed.'
            : 'The client will be told.';
      setMessage(pendingChange || booking?.dateConfirmed
        ? `Shoot updated. ${noticeLine}`.trim()
        : `Shoot confirmed. ${noticeLine}`.trim());
      if (notice && !notice.duplicate && notice.type) {
        setFollowUp({
          emailStatus: notice.status,
          kind: notice.type === 'shoot-confirmed' ? 'confirmed' : 'updated',
          shootDate: nextBooking.shootDate,
          startTime: nextBooking.startTime,
          locationName: nextBooking.locationName,
          address: nextBooking.address,
          mapsUrl: nextBooking.mapsUrl,
          quoteNumber: nextBooking.quoteNumber || quote.quoteNumber,
          link: notice.link,
          sessions: nextBooking.sessions,
          failedNoticeId: notice.status === 'failed' ? notice.id : '',
          noticeLine,
        });
      }
      if (!options?.fromQuoted) setTab('booking');
    } catch {
      setError('The shoot could not be confirmed.');
      setTab('booking');
    } finally {
      setConfirming(false);
    }
  };

  const spokenCount = useMemo(
    () => timeline.filter((entry) => entry.text && entry.kind !== 'internal' && entry.kind !== 'whatsapp-note').length,
    [timeline],
  );
  const clientWaiting = (() => {
    const last = [...timeline].reverse().find((entry) => entry.kind === 'client-message' || entry.kind === 'studio-reply');
    return last?.kind === 'client-message' || last?.sender === 'client';
  })();

  const moneyHint = !quote
    ? 'No quotation yet'
    : (summary?.outstandingMinor || 0) <= 0 && (summary?.receivedMinor || 0) > 0
      ? 'Paid in full'
      : (summary?.currentlyDueMinor || summary?.depositDueMinor || summary?.outstandingMinor || 0) > 0
        ? `${money(summary?.currentlyDueMinor || summary?.depositDueMinor || summary?.outstandingMinor)} due`
        : money(quote.snapshot?.quoteTotalMinor, quote.snapshot?.currency || 'R');

  const bookingHint = booking?.dateConfirmed && booking?.shootDate
    ? `Confirmed ${shortDate(booking.shootDate)}`
    : booking?.proposedShootDate || booking?.shootDate
      ? `To confirm · ${shortDate(booking.proposedShootDate || booking.shootDate)}`
      : quote?.preferredDate
        ? `Preferred ${formatPreferredDate(quote.preferredDate)}`
        : 'Date to agree';

  const messagesHint = clientWaiting
    ? 'Client waiting'
    : spokenCount > 0
      ? `${spokenCount} message${spokenCount === 1 ? '' : 's'}`
      : 'No messages yet';

  const tabs: Array<{ id: ShootTab; label: string; short: string; hint: string; fresh?: boolean; testId: string }> = [
    { id: 'money', label: 'Money', short: 'Money', hint: moneyHint, testId: 'workspace-money' },
    { id: 'booking', label: 'Calendar', short: 'Date', hint: bookingHint, testId: 'workspace-booking' },
    { id: 'conversation', label: 'Messages', short: 'Messages', hint: messagesHint, fresh: clientWaiting, testId: 'workspace-conversation' },
    { id: 'details', label: 'Client', short: 'Client', hint: contactEmail || contactPhone || 'Contact', testId: 'workspace-details' },
    { id: 'history', label: 'History', short: 'History', hint: 'Every change', testId: 'workspace-history' },
  ];

  const otherShoots = relatedQuotes.filter((item) => item.id !== quote?.id);

  const displayName = contactName || quote?.client?.name || latestEnquiry?.name || 'Client';

  const acceptedQuote = quote?.status === 'accepted';
  const depositDone = Boolean(summary && isFinanciallySecured(summary));
  const confirmed = Boolean(booking?.dateConfirmed && booking?.shootDate);
  const finished = Boolean(booking?.completedAt);
  const delivered = Boolean(booking?.deliveredAt);
  const closed = Boolean(booking?.closedAt);
  const quotedDone = Boolean(quote && quote.status !== 'draft');
  const stepDone: Record<FlowStepId, boolean> = {
    enquiry: Boolean(quote),
    quoted: quotedDone,
    accepted: Boolean(acceptedQuote),
    deposit: depositDone,
    confirm: confirmed,
    finish: finished,
    editing: delivered,
    delivered,
    closed,
  };
  const currentStep: FlowStepId = !quote
    ? 'enquiry'
    : quote.status === 'draft'
      ? 'quoted'
      : !acceptedQuote
        ? 'quoted'
        : !confirmed
          ? 'confirm'
          : !finished
            ? 'finish'
            : !delivered
              ? 'editing'
              : !closed
                ? 'delivered'
                : 'closed';
  const flowSteps = FLOW_STEPS.map((step) => ({
    ...step,
    done: stepDone[step.id],
    current: step.id === currentStep,
    locked: FLOW_RANK.indexOf(step.id) > FLOW_RANK.indexOf(currentStep),
  }));

  /** Keeps the page tall enough for the current scroll so a shorter panel cannot pull the stage strip. */
  const holdScrollPosition = () => {
    const el = workspaceRef.current;
    if (!el) return;
    el.style.minHeight = '';
  };

  const openFlowStep = (id: FlowStepId) => {
    if (FLOW_RANK.indexOf(id) > FLOW_RANK.indexOf(currentStep)) return;
    const go = () => {
      holdScrollPosition();
      setStageView((current) => {
        const next = current === id ? null : id;
        if (next) {
          const workspace = STAGE_WORKSPACE[next];
          if ('tabs' in workspace && workspace.defaultTab) {
            setTabState(workspace.defaultTab);
            onTabChange?.(workspace.defaultTab);
          }
          if (next === 'deposit') setReceiptAsk((count) => count + 1);
        }
        return next;
      });
    };
    if (stageViewRef.current === 'quoted') requestLeaveRef.current(go);
    else go();
  };

  useEffect(() => {
    if (!onLeaveGuard) return undefined;
    onLeaveGuard((proceed) => requestLeaveRef.current(proceed));
    return () => onLeaveGuard(null);
  }, [onLeaveGuard]);

  const saveAndLeave = async () => {
    const editor = quoteEditorRef.current;
    const proceed = leavePrompt;
    if (!editor) {
      setLeavePrompt(null);
      proceed?.();
      return;
    }
    setLeaveBusy(true);
    setLeaveError('');
    const saved = await editor.save();
    setLeaveBusy(false);
    if (!saved) {
      setLeaveError(editor.error || 'The quotation could not be saved.');
      return;
    }
    setLeavePrompt(null);
    proceed?.();
  };

  const discardAndLeave = () => {
    quoteEditorRef.current?.discard();
    const proceed = leavePrompt;
    setLeaveError('');
    setLeavePrompt(null);
    proceed?.();
  };

  const showProgress = currentStep === 'finish' || currentStep === 'editing' || currentStep === 'closed';

  if (loading) {
    return (
      <div ref={workspaceRef} className="studio-home client-workspace shoot-file" data-testid="client-workspace">
        <p className="studio-empty">Loading shoot…</p>
      </div>
    );
  }

  const stageOpen = stageView;
  const stageWorkspace = stageOpen ? STAGE_WORKSPACE[stageOpen] : null;
  const stageTabs = stageWorkspace && 'tabs' in stageWorkspace ? stageWorkspace.tabs : null;
  /** Free workspace when no stage is open: Messages / History; Money and Calendar after accept. */
  const freeTabs = tabs.filter((item) => {
    if (item.id === 'booking') return acceptedQuote;
    if (item.id === 'details') return false;
    if (item.id === 'money') return acceptedQuote;
    if (item.id === 'conversation' || item.id === 'history') return Boolean(quote);
    return true;
  });
  const visibleTabs = stageTabs
    ? tabs.filter((item) => stageTabs.includes(item.id))
    : freeTabs;
  const showAcceptPanel = stageOpen === 'accepted';
  const showWrapPanel = Boolean(
    stageWorkspace && 'mode' in stageWorkspace && stageWorkspace.mode === 'wrap'
  ) || (stageOpen == null && showProgress);
  const showQuotedPanel = stageOpen === 'quoted';
  const showDepositPanel = stageOpen === 'deposit';
  const showMoneyPanel = (stageOpen == null && tab === 'money' && Boolean(quote) && acceptedQuote);
  const showBookingPanel = stageOpen === 'confirm' || (stageOpen == null && tab === 'booking' && acceptedQuote);
  const showConversationPanel = stageOpen == null && tab === 'conversation' && Boolean(quote);
  const showHistoryPanel = stageOpen == null && tab === 'history' && Boolean(quote);
  const showClientPanel = stageOpen === 'enquiry';
  const showTabStrip = visibleTabs.length > 1 || (stageOpen == null && visibleTabs.length > 0);
  const showStageNextStep = stageOpen !== 'enquiry'
    && stageOpen !== 'accepted'
    && stageOpen !== 'quoted'
    && stageOpen !== 'deposit';

  return (
    <div ref={workspaceRef} className="studio-home client-workspace shoot-file" data-testid="client-workspace">
      <header className="studio-home-head shoot-file-head">
        <div>
          <button type="button" className="studio-text-action" onClick={() => requestLeaveRef.current(onBack)} data-testid="shoot-back">
            {backLabel}
          </button>
          <h2>{displayName}</h2>
          <p className="shoot-file-package">
            {quote
              ? `${quote.quoteNumber} · ${packageName} · ${quote.status}`
              : latestEnquiry
                ? `Client details · ${latestEnquiry.source}${latestEnquiry.shootType ? ` · ${latestEnquiry.shootType}` : ''}`
                : 'No quotation yet'}
            {company ? ` · ${company}` : ''}
          </p>
          {(contactPhone || contactEmail) ? (
            <div className="studio-contact-actions" data-testid="contact-actions">
              {contactPhoneE164 ? <a className="studio-secondary" href={`tel:${contactPhoneE164}`}>Call</a> : null}
              {contactPhoneE164 ? (
                <a className="studio-secondary" href={buildStudioWhatsAppUrl(contactPhoneE164, '')} target="_blank" rel="noopener noreferrer">WhatsApp</a>
              ) : null}
              {contactEmail ? <a className="studio-secondary" href={`mailto:${contactEmail}`}>Email</a> : null}
            </div>
          ) : null}
          {otherShoots.length > 0 ? (
            <div className="shoot-file-others" data-testid="other-shoots">
              <small>Other shoots</small>
              {otherShoots.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="studio-text-action"
                  title={[item.quoteNumber, item.preferredDate, item.shootType, item.status].filter(Boolean).join(' · ')}
                  onClick={() => requestLeaveRef.current(() => onOpenShoot?.(item.id, 'money'))}
                >
                  {otherShootLabel(item)}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </header>

      <div className="shoot-sticky-chrome shoot-progress" data-testid="shoot-sticky-chrome">
        <ol className="shoot-stage-strip" aria-label="Shoot progress" data-testid="shoot-stage-strip">
          {flowSteps.map((step, index) => {
            const editingLive = step.id === 'editing' && journey.stage === 'editing';
            const clickable = !step.locked;
            const isOpen = stageOpen === step.id;
            const stepNumber = String(index + 1).padStart(2, '0');
            const state = [
              step.done ? 'is-done' : '',
              step.current ? 'is-current' : '',
              isOpen ? 'is-open' : '',
              editingLive ? 'is-editing' : '',
              editingLive && journey.deliveryOverdue ? 'is-overdue' : '',
              clickable ? 'is-clickable' : 'is-locked',
            ].filter(Boolean).join(' ');
            return (
              <li
                key={step.id}
                className={state}
                aria-current={step.current && !isOpen ? 'step' : undefined}
              >
                <button
                  type="button"
                  onClick={() => openFlowStep(step.id)}
                  disabled={step.locked}
                  data-testid={step.id === 'enquiry' ? 'open-enquiry' : step.id === 'confirm' ? 'open-date-confirmed' : `open-stage-${step.id}`}
                  aria-pressed={isOpen}
                  title={
                    step.locked
                      ? `${step.label} is still ahead`
                      : editingLive && journey.deliveryLine
                        ? journey.deliveryLine
                        : isOpen
                          ? `Close ${step.label}`
                          : step.done
                            ? `${step.label} is done. Open it to change details.`
                            : `Open ${step.label}`
                  }
                >
                  <span className="shoot-stage-diamond" aria-hidden="true">
                    <span className="shoot-stage-num">{stepNumber}</span>
                  </span>
                  <span className="shoot-stage-label">{step.label}</span>
                  {editingLive && journey.daysToDelivery !== null ? (
                    <em data-testid="stage-days-left">
                      {journey.daysToDelivery < 0 ? `${Math.abs(journey.daysToDelivery)}d over` : `${journey.daysToDelivery}d left`}
                    </em>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ol>
        {showQuotedPanel && quoteEditor ? (
          <div className="shoot-quote-save" data-testid="quote-save-bar">
            <span>{quoteEditor.dirty ? 'Unsaved changes' : 'Saved'}</span>
            <button
              type="button"
              className="studio-primary"
              data-testid="save-quote"
              disabled={!quoteEditor.dirty || quoteEditor.busy}
              onClick={() => { void quoteEditor.save(); }}
            >
              {quoteEditor.busy ? 'Saving…' : 'Save quotation'}
            </button>
          </div>
        ) : null}
      </div>

      {showStageNextStep ? (
      <section className={`studio-next-step${showNextButton || acceptedQuote ? '' : ' is-quiet'}`} aria-label="Next step" data-testid="next-step">
        <div>
          <small>{journey.needsAction ? 'Next step' : 'Status'}</small>
          <strong data-testid="journey-status">{journey.journeyStatus}</strong>
          <span data-testid="journey-next">{journey.nextAction}</span>
          {journey.nextButton === 'Confirm shoot' && tab !== 'booking' && confirmPreview ? (
            <span data-testid="confirm-shoot-preview">{confirmPreview}</span>
          ) : null}
        </div>
        {showNextButton && !(journey.nextButton === 'Confirm shoot' && tab === 'booking') ? (
          <div className="studio-toolbar">
            <button
              type="button"
              className="studio-primary"
              onClick={runNextStep}
              disabled={confirming}
              data-testid={journey.nextButton === 'Confirm shoot' ? 'confirm-shoot-now' : (!quote ? 'prepare-quotation' : quote.status === 'draft' ? 'edit-draft-overview' : 'next-step-button')}
            >
              {confirming ? 'Confirming…' : journey.nextButton}
            </button>
            {journey.nextButton === 'Confirm shoot' ? (
              <button type="button" className="studio-text-action" onClick={() => setTab('booking')}>Change details</button>
            ) : null}
          </div>
        ) : null}
      </section>
      ) : null}

      {showWrapPanel ? jobCompletePanel : null}

      {showAcceptPanel ? (
        <section className="studio-panel" data-testid="accept-from-email" aria-label="Accepted">
          <h3 className="studio-section-label">Accepted</h3>
          {acceptedQuote ? (
            <>
              <p className="studio-note">
                This quotation is accepted. The total is locked.
                {!confirmed
                  ? depositDone
                    ? ' Deposit is in. Confirm the shoot when the date is set.'
                    : ' You can confirm the shoot now, or record the deposit first — either order is fine.'
                  : depositDone
                    ? ' Deposit is in and the shoot is confirmed.'
                    : ' The shoot is confirmed. Deposit can still be recorded anytime.'}
              </p>
              <div className="studio-toolbar">
                {!depositDone ? (
                  <button type="button" className="studio-secondary" onClick={() => openFlowStep('deposit')}>
                    Open deposit
                  </button>
                ) : null}
                {!confirmed ? (
                  <button type="button" className="studio-primary" onClick={() => openFlowStep('confirm')}>
                    Confirm shoot
                  </button>
                ) : null}
              </div>
            </>
          ) : (
            <p className="studio-note">
              Mark acceptance from the Quoted stage once the client replies, or wait for them to accept on their link.
            </p>
          )}
        </section>
      ) : null}

      {reminders.length > 0 ? (
        <div className="studio-reminder-list shoot-file-reminders" data-testid="workspace-reminders">
          <h3 className="studio-section-label">Suggested follow-ups</h3>
          {reminders.map((row) => (
            <div className="studio-attention-row" key={row.id}>
              <strong>{row.label}</strong>
              <span>{row.reason}</span>
              <div className="studio-toolbar">
                <button type="button" className="studio-primary" onClick={() => actOnReminder('send', row)}>Send</button>
                <button type="button" className="studio-secondary" onClick={() => actOnReminder('snooze', row)}>Snooze</button>
                <button type="button" className="studio-text-action" onClick={() => actOnReminder('dismiss', row)}>Dismiss</button>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      {pendingReminder ? (
        <NotifyChannelPrompt
          title={`Send reminder: ${pendingReminder.label}`}
          detail={pendingReminder.reason}
          preference={quote?.client?.preference}
          hasEmail={Boolean(contactEmail)}
          hasPhone={Boolean(contactPhoneE164)}
          allowSkip
          whatsAppUrl={buildStudioWhatsAppUrl(
            contactPhoneE164,
            reminderWhatsAppText(pendingReminder.label, quote?.quoteNumber, {
              link: pendingReminder.link || conversationMeta?.clientLink,
              noticeType: pendingReminder.noticeType,
              confirmed: Boolean(booking?.dateConfirmed),
            }),
          )}
          onEmail={() => sendReminderViaApi(pendingReminder, false)}
          onDone={async (channelSummary) => {
            if (channelSummary.channel === 'whatsapp') {
              await sendReminderViaApi(pendingReminder, true);
            }
            setMessage(channelSummary.lines.join(' '));
            setPendingReminder(null);
            if (quote?.id) await loadConversation(quote.id);
            await loadReminders(quote?.id);
          }}
          onDismiss={() => setPendingReminder(null)}
        />
      ) : null}

      {showTabStrip ? (
        <div className="studio-view-tabs client-workspace-tabs shoot-file-tabs" role="tablist" aria-label="Shoot sections">
          {visibleTabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              id={`shoot-tab-${item.id}`}
              aria-selected={tab === item.id}
              className={`shoot-tab${tab === item.id ? ' is-active' : ''}${item.fresh ? ' is-fresh' : ''}`}
              onClick={() => setTab(item.id)}
            >
              <span className="shoot-tab-long">{item.label}</span>
              <span className="shoot-tab-short">{item.short}</span>
              <small className="shoot-tab-hint">{item.hint}</small>
            </button>
          ))}
        </div>
      ) : null}

      {error ? <p className="studio-error" role="alert">{error}</p> : null}
      {message ? <p className="studio-note">{message}</p> : null}

      {stageOpen === 'enquiry' ? (
        <div className="shoot-enquiry-top" data-testid="shoot-enquiry-top">
          <EnquiryRecord enquiries={enquiries} />
        </div>
      ) : null}

      {showConversationPanel ? (
        <section className="studio-panel" data-testid="workspace-conversation" role="tabpanel" aria-labelledby="shoot-tab-conversation">
          {quote ? (
            <>
              <p className="studio-note">
                This is the studio conversation. Apple Mail and WhatsApp are not synchronised here.
                {conversationMeta?.preference ? ` Client preference: ${conversationMeta.preference}.` : ''}
              </p>
              <CorrespondenceTimeline entries={timeline} separateInternal studio chat />
              {sentReply ? (
                <div data-testid="conversation-delivery-status">
                  <ReplyReach
                    sent={sentReply}
                    clientName={displayName}
                    phoneE164={contactPhoneE164}
                    clientLink={conversationMeta?.clientLink}
                    booking={Boolean(booking?.dateConfirmed)}
                    onRecord={(text) => saveNote('whatsapp-note', text, () => undefined)}
                    onClose={() => setSentReply(null)}
                  />
                </div>
              ) : null}
              <div className="chat-composer">
                <textarea
                  rows={1}
                  value={reply}
                  maxLength={2000}
                  placeholder="Write a message to the client"
                  onChange={(event) => setReply(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
                      event.preventDefault();
                      sendReply();
                    }
                  }}
                  aria-label="Message to client"
                />
                <button type="button" className="chat-send" onClick={sendReply} data-testid="send-client-message" disabled={sendingReplyUi}>Send</button>
              </div>
              <p className="chat-hint">
                The client sees this on their quotation page, and it is emailed when delivery is switched on. Ctrl+Enter sends.
              </p>
              <details className="studio-private-note">
                <summary>Add a private note (only you will see it)</summary>
                <label className="studio-compose">
                  Private note
                  <textarea value={note} maxLength={2000} onChange={(event) => setNote(event.target.value)} aria-label="Private note" />
                </label>
                <div className="studio-toolbar">
                  <button type="button" className="studio-secondary" onClick={() => saveNote('internal', note, () => setNote(''))}>Save private note</button>
                  <button type="button" className="studio-secondary" onClick={() => saveNote('whatsapp-note', note, () => setNote(''))}>Record a WhatsApp chat instead</button>
                </div>
              </details>
            </>
          ) : (
            <p className="studio-empty">Conversation follows a quotation. Prepare a quotation from this enquiry first.</p>
          )}
        </section>
      ) : null}

      {showBookingPanel ? (
        <section data-testid="workspace-booking" role="tabpanel" aria-labelledby="shoot-tab-booking">
          {quote?.status === 'accepted' ? (
            <BookingEditor
              booking={booking || {
                quoteId: quote.id,
                quoteNumber: quote.quoteNumber,
                clientName: displayName,
                shootType: quote.shootType || '',
                timezone: 'Africa/Johannesburg',
              }}
              embedded
              preferredTime={quote.preferredTime || ''}
              clientPhone={contactPhoneE164}
              clientEmail={contactEmail}
              clientPreference={quote.client?.preference}
              followUp={followUp}
              onFollowUpHandled={() => setFollowUp(null)}
              onSaved={(next, savedNote) => {
                setBooking(next);
                setMessage(savedNote);
                if (quote?.id) loadConversation(quote.id).catch(() => undefined);
              }}
            />
          ) : quote ? (
            <ShootRequestEditor
              quote={quote}
              onSaved={(next) => {
                setQuote(next);
                setMessage('Date and place saved on this quotation.');
              }}
            />
          ) : (
            <p className="studio-empty studio-panel">Prepare a quotation before setting a date and place.</p>
          )}
          {onOpenCalendar ? (
            <div className="studio-toolbar">
              <button type="button" className="studio-secondary" onClick={() => requestLeaveRef.current(() => onOpenCalendar())}>Open full calendar</button>
            </div>
          ) : null}
        </section>
      ) : null}

      {showQuotedPanel ? (
        <section data-testid="workspace-quoted" aria-label="Quoted">
          {quote ? (
            <>
              <StudioQuoteRecord
                quote={quote}
                summary={summary}
                variant="quote"
                onEditorChange={setQuoteEditor}
                onQuoteUpdated={(next) => {
                  setQuote(next);
                  loadPaymentSummary(next.id).catch(() => undefined);
                }}
              />
              {quote.status === 'issued' && !quote.deletedAt && !quote.archivedAt ? (
                <section className="studio-panel" data-testid="accept-from-email" aria-label="Mark accepted" style={{ marginTop: '1.25rem' }}>
                  <h3 className="studio-section-label">Client response</h3>
                  <p className="studio-note">
                    The client can accept on their link. If they accepted by email, record it here. That locks the total. It does not record a payment.
                  </p>
                  <label className="studio-compose">
                    Note from their email (optional)
                    <textarea
                      value={emailAcceptanceNote}
                      maxLength={500}
                      rows={3}
                      aria-label="Note from their email"
                      onChange={(event) => setEmailAcceptanceNote(event.target.value)}
                    />
                  </label>
                  <button
                    type="button"
                    className="studio-accent"
                    data-testid="record-email-acceptance"
                    disabled={recordingAcceptance}
                    onClick={recordEmailAcceptance}
                  >
                    {recordingAcceptance ? 'Recording…' : 'Mark accepted'}
                  </button>
                </section>
              ) : null}
            </>
          ) : (
            <div className="studio-panel studio-enquiry-next" data-testid="enquiry-next">
              <p className="studio-empty">
                No quotation yet. Open Client details to prepare one.
                {latestEnquiry && onOpenBuilder ? (
                  <>
                    {' '}
                    <button type="button" className="studio-text-action" data-testid="prepare-quotation" onClick={() => prepareFromEnquiry(latestEnquiry)}>
                      Prepare quotation
                    </button>
                  </>
                ) : null}
              </p>
            </div>
          )}
        </section>
      ) : null}

      {showDepositPanel ? (
        <section data-testid="workspace-deposit" aria-label="Deposit paid">
          {quote ? (
            <StudioQuoteRecord
              quote={quote}
              summary={summary}
              variant="deposit"
              askForReceipt={receiptAsk}
              onQuoteUpdated={(next) => {
                setQuote(next);
                loadPaymentSummary(next.id).catch(() => undefined);
              }}
              onReceiptRecorded={() => {
                loadPaymentSummary(quote.id).catch(() => undefined);
                loadConversation(quote.id).catch(() => undefined);
                loadReviewRequest(quote.id).catch(() => undefined);
                onPaymentsChanged?.();
              }}
            />
          ) : (
            <p className="studio-empty studio-panel">Accept a quotation before recording a deposit.</p>
          )}
          {quote && !confirmed ? (
            <div className="studio-toolbar" style={{ marginTop: '1rem' }}>
              <button type="button" className="studio-primary" onClick={() => openFlowStep('confirm')}>
                Confirm shoot
              </button>
            </div>
          ) : null}
        </section>
      ) : null}

      {showMoneyPanel ? (
        <section data-testid="workspace-money" role="tabpanel" aria-labelledby="shoot-tab-money">
          {quote ? (
            <StudioQuoteRecord
              quote={quote}
              summary={summary}
              variant="deposit"
              askForReceipt={receiptAsk}
              onQuoteUpdated={(next) => {
                setQuote(next);
                loadPaymentSummary(next.id).catch(() => undefined);
              }}
              onReceiptRecorded={() => {
                loadPaymentSummary(quote.id).catch(() => undefined);
                loadConversation(quote.id).catch(() => undefined);
                loadReviewRequest(quote.id).catch(() => undefined);
                onPaymentsChanged?.();
              }}
            />
          ) : null}
        </section>
      ) : null}

      {showHistoryPanel ? (
        <JobHistory
          quote={quote}
          timeline={timeline}
          enquiries={enquiries}
          payments={summary ? summary.payments : null}
          arrangement={summary?.arrangement}
          onSent={() => { if (quote?.id) loadConversation(quote.id).catch(() => undefined); }}
        />
      ) : null}

      {showClientPanel ? (
        <section className="studio-panel studio-panel-editable" data-testid="workspace-details" role="tabpanel" aria-labelledby="shoot-tab-details">
          <div className="studio-panel-body">
            <h3 className="studio-section-label">Client</h3>
            {(contactName || contactEmail || contactPhone || company || billingAddress || quote || latestEnquiry) ? (
              editingContact ? (
                <div className="studio-contact-grid">
                  <label>
                    Name
                    <input
                      type="text"
                      name="name"
                      autoComplete="name"
                      aria-label="Name"
                      value={contactName}
                      onChange={(event) => setContactName(event.target.value)}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      inputMode="email"
                      aria-label="Email"
                      value={contactEmail}
                      onChange={(event) => setContactEmail(event.target.value)}
                    />
                  </label>
                  <div className="studio-phone-fields">
                    <label>
                      Country
                      <CountryCodeSelect
                        id="contact-country-code"
                        value={contactCountryCode}
                        onChange={setContactCountryCode}
                      />
                    </label>
                    <label>
                      Mobile
                      <input
                        type="tel"
                        name="tel"
                        autoComplete="tel"
                        inputMode="tel"
                        enterKeyHint="done"
                        aria-label="Mobile"
                        placeholder="082 123 4567"
                        value={contactPhone}
                        onChange={(event) => setContactPhone(event.target.value)}
                        data-testid="contact-mobile"
                      />
                    </label>
                  </div>
                  {(company || quote) ? (
                    <label>
                      Company
                      <input
                        type="text"
                        name="organization"
                        autoComplete="organization"
                        aria-label="Company"
                        value={company}
                        onChange={(event) => setCompany(event.target.value)}
                      />
                    </label>
                  ) : null}
                  {(billingAddress || quote) ? (
                    <label className="studio-billing-address">
                      Billing address
                      <textarea
                        aria-label="Billing address"
                        name="street-address"
                        autoComplete="street-address"
                        value={billingAddress}
                        rows={4}
                        maxLength={500}
                        onChange={(event) => setBillingAddress(event.target.value)}
                      />
                    </label>
                  ) : null}
                  {quote ? (
                    <div className="studio-toolbar">
                      <button type="button" className="studio-primary" onClick={saveContact}>Save contact</button>
                      <button type="button" className="studio-secondary" onClick={() => setEditingContact(false)}>Cancel</button>
                    </div>
                  ) : (
                    <button type="button" className="studio-secondary" onClick={() => setEditingContact(false)}>Done</button>
                  )}
                </div>
              ) : (
                <dl className="studio-summary-list" data-testid="contact-summary">
                  {contactName ? <div><dt>Name</dt><dd>{contactName}</dd></div> : null}
                  {contactEmail ? <div><dt>Email</dt><dd>{contactEmail}</dd></div> : null}
                  {contactPhone ? (
                    <div>
                      <dt>Mobile</dt>
                      <dd>{contactCountryCode} {contactPhone}</dd>
                    </div>
                  ) : null}
                  {company ? <div><dt>Company</dt><dd>{company}</dd></div> : null}
                  {billingAddress ? <div><dt>Billing address</dt><dd className="studio-billing-address">{billingAddress}</dd></div> : null}
                  {!contactName && !contactEmail && !contactPhone && !company && !billingAddress ? (
                    <div><dt>Contact</dt><dd>No details saved yet</dd></div>
                  ) : null}
                </dl>
              )
            ) : (
              <p className="studio-note">No client details yet.</p>
            )}
            {!quote && latestEnquiry && onOpenBuilder ? (
              <div className="studio-toolbar" style={{ marginTop: '1rem' }}>
                <button type="button" className="studio-primary" data-testid="prepare-quotation" onClick={() => prepareFromEnquiry(latestEnquiry)}>
                  Prepare quotation
                </button>
              </div>
            ) : null}
            {!quote && latestEnquiry && !latestEnquiry.linkedQuoteId ? (
              confirmDeleteEnquiry ? (
                <div className="studio-confirm-strip" role="alertdialog" aria-label="Delete this enquiry" data-testid="delete-enquiry-confirm">
                  <span>Delete this enquiry from {latestEnquiry.name || 'this person'}? It is removed for good and nothing is sent to them.</span>
                  <div className="studio-toolbar">
                    <button type="button" className="studio-danger" onClick={deleteEnquiry} disabled={deleteEnquiryBusy} data-testid="delete-enquiry-yes">
                      {deleteEnquiryBusy ? 'Deleting…' : 'Yes, delete it'}
                    </button>
                    <button type="button" className="studio-secondary" onClick={() => setConfirmDeleteEnquiry(false)} disabled={deleteEnquiryBusy}>Keep it</button>
                  </div>
                </div>
              ) : (
                <p className="studio-note studio-enquiry-drop">
                  Not going ahead?{' '}
                  <button type="button" className="studio-text-action" onClick={() => setConfirmDeleteEnquiry(true)} data-testid="delete-enquiry">
                    Delete this enquiry
                  </button>
                </p>
              )
            ) : null}
            {conversationMeta?.clientLink ? (
              <p className="studio-note">
                Client link:{' '}
                <button type="button" className="studio-text-action" onClick={() => navigator.clipboard.writeText(conversationMeta.clientLink || '')}>
                  Copy client link
                </button>
              </p>
            ) : null}
          </div>
          {(quote || latestEnquiry) ? (
            <div className="studio-panel-action">
              <button
                type="button"
                className={`studio-icon-action${editingContact ? ' is-active' : ''}`}
                aria-label={editingContact ? 'Close contact editor' : 'Edit client details'}
                title={editingContact ? 'Close' : 'Edit'}
                data-testid="edit-client-details"
                onClick={() => setEditingContact((open) => !open)}
              >
                {editingContact ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                  </svg>
                )}
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
      {mailAsk ? (
        <ClientSendPrompt
          title={mailAsk.title}
          detail={mailAsk.detail}
          busy={confirming || deliverBusy}
          onSend={mailAsk.send}
          onSkip={mailAsk.skip}
        />
      ) : null}
      {leavePrompt ? (
        <div className="studio-leave-backdrop" role="presentation">
          <div className="studio-leave-dialog" role="dialog" aria-labelledby="quote-leave-title" data-testid="quote-leave-dialog">
            <h3 id="quote-leave-title">Save this quotation?</h3>
            <p>There are unsaved changes. Save them, or discard them and leave.</p>
            {leaveError ? <p className="studio-note" role="alert">{leaveError}</p> : null}
            <div className="studio-toolbar">
              <button type="button" className="studio-primary" data-testid="quote-leave-save" disabled={leaveBusy} onClick={() => { void saveAndLeave(); }}>
                {leaveBusy ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className="studio-secondary" data-testid="quote-leave-discard" disabled={leaveBusy} onClick={discardAndLeave}>
                Discard
              </button>
              <button type="button" className="studio-text-action" disabled={leaveBusy} onClick={() => { setLeavePrompt(null); setLeaveError(''); }}>
                Stay
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ShootFile;
